import CargoCompanyOrderBillingsDownloadParams from "../../../../../common/services/cargo-company/order-billings/download/cargo-company-order-billings-download-params";
import BillingStatus from "../../../../../common/types/billing-status";
import CargoCompanyOrder from "../../../../../common/types/cargo-company-order";
import BillingsCargoOrderListingFilter, {
  BillingsCargoOrderListingBillingStatusFilter,
  BillingsCargoOrderListingDispatchFilter,
  BillingsCargoOrderListingExternalOrderIdFilter,
  BillingsCargoOrderListingInternalOrderIdFilter,
  BillingsCargoOrderListingOrderStartDateFilter,
  BillingsCargoOrderListingPassengerFilter,
  BillingsCargoOrderListingRouteAddressFilter,
  BillingsCargoOrderListingRouteDestinationAddressFilter,
  BillingsCargoOrderListingRouteIntermediateAddressFilter,
  BillingsCargoOrderListingRoutePickupAddressFilter,
  BillingsCargoOrderListingTaxiFilter,
} from "./types/billings-cargo-order-listing-filter";
import BillingsCargoOrderListingFilterType from "./types/billings-cargo-order-listing-filter-type";
import BillingsCargoOrderListingItemBillingStatus from "./types/billings-cargo-order-listing-item-billing-status";
import BillingsCargoOrderListingSortKey from "./types/billings-cargo-order-listing-sort-key";

const createStatus = (
  status: BillingsCargoOrderListingItemBillingStatus | undefined,
): BillingStatus | undefined => {
  switch (status) {
    case BillingsCargoOrderListingItemBillingStatus.CREATED:
      return BillingStatus.CREATED;
    case BillingsCargoOrderListingItemBillingStatus.ACCEPTED:
      return BillingStatus.ACCEPTED;
    case BillingsCargoOrderListingItemBillingStatus.REJECTED:
      return BillingStatus.REJECTED;
    case BillingsCargoOrderListingItemBillingStatus.REOPENED:
      return BillingStatus.REOPENED;
    case BillingsCargoOrderListingItemBillingStatus.REOPEN_REQUEST_SENT:
      return BillingStatus.REOPEN_REQUEST;
    default:
      return undefined;
  }
};

const createRequestOrder = (
  sortKey: BillingsCargoOrderListingSortKey | null,
): CargoCompanyOrderBillingsDownloadParams["order"] => {
  const options: {
    sortKey: BillingsCargoOrderListingSortKey;
    requestOrder: CargoCompanyOrder;
  }[] = [
    {
      requestOrder: CargoCompanyOrder.DATE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.ORDER_START_DATE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DATE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.ORDER_START_DATE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HUMAN_ID_ASC,
      sortKey: BillingsCargoOrderListingSortKey.INTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HUMAN_ID_DESC,
      sortKey: BillingsCargoOrderListingSortKey.INTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.TAXI_CORPORATION_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TAXI_CORPORATION_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.TAXI_CORPORATION_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TAXI_CORPORATION_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.EXTERNAL_ORDER_ID_ASC,
      sortKey: BillingsCargoOrderListingSortKey.EXTERNAL_ORDER_ID_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.EXTERNAL_ORDER_ID_DESC,
      sortKey: BillingsCargoOrderListingSortKey.EXTERNAL_ORDER_ID_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISPATCH_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISPATCH_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISPATCH_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISPATCH_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISTANCE_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISTANCE_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_HOURS_ASC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_TIME_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_HOURS_DESC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_TIME_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HALTING_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.STOPOVER_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.HIGHWAY_CHARGE_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TOLL_ROADS_COST_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_SAVINGS_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_SAVINGS_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_AMOUNT_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.DISCOUNT_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.DISCOUNT_AMOUNT_DESC,
    },
    {
      requestOrder: CargoCompanyOrder.TOTAL_AMOUNT_ASC,
      sortKey: BillingsCargoOrderListingSortKey.TOTAL_COST_ASC,
    },
    {
      requestOrder: CargoCompanyOrder.TOTAL_AMOUNT_DESC,
      sortKey: BillingsCargoOrderListingSortKey.TOTAL_COST_DESC,
    },
  ];

  return options.find((option) => option.sortKey === sortKey)?.requestOrder;
};

const createRequest = (
  filters: BillingsCargoOrderListingFilter[],
  sortKey: BillingsCargoOrderListingSortKey | null,
  company_id: string,
): CargoCompanyOrderBillingsDownloadParams => {
  return {
    company_id,
    order: createRequestOrder(sortKey),
    human_id: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.INTERNAL_ORDER_ID,
    )?.value as
      | BillingsCargoOrderListingInternalOrderIdFilter["value"]
      | undefined,
    address: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.ROUTE_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteAddressFilter["value"]
      | undefined,
    start_address: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_PICKUP_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRoutePickupAddressFilter["value"]
      | undefined,
    mid_address: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_INTERMEDIATE_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteIntermediateAddressFilter["value"]
      | undefined,
    end_address: filters.find(
      (filter) =>
        filter.type ===
        BillingsCargoOrderListingFilterType.ROUTE_DESTINATION_ADDRESS,
    )?.value as
      | BillingsCargoOrderListingRouteDestinationAddressFilter["value"]
      | undefined,
    passenger: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.PASSENGER,
    )?.value as BillingsCargoOrderListingPassengerFilter["value"] | undefined,
    taxi: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.TAXI,
    )?.value as BillingsCargoOrderListingTaxiFilter["value"] | undefined,
    external_id: filters.find(
      (filter) =>
        filter.type === BillingsCargoOrderListingFilterType.EXTERNAL_ORDER_ID,
    )?.value as
      | BillingsCargoOrderListingExternalOrderIdFilter["value"]
      | undefined,
    dispatch: filters.find(
      (filter) => filter.type === BillingsCargoOrderListingFilterType.DISPATCH,
    )?.value as BillingsCargoOrderListingDispatchFilter["value"] | undefined,
    status: createStatus(
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.BILLING_STATUS,
      )?.value as
        | BillingsCargoOrderListingBillingStatusFilter["value"]
        | undefined,
    ),
    date_since: (
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsCargoOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.from.toJSON(),
    date_to: (
      filters.find(
        (filter) =>
          filter.type === BillingsCargoOrderListingFilterType.ORDER_START_DATE,
      )?.value as
        | BillingsCargoOrderListingOrderStartDateFilter["value"]
        | undefined
    )?.to.toJSON(),
  };
};

const billingsCargoOrderListingReportRequestFactory = {
  createRequest,
};

export default billingsCargoOrderListingReportRequestFactory;
